import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import { useChatWebSocket } from '../../hooks/useChatWebSocket';
import { useAuthState } from '../../providers/AuthProvider';
import { ChatInput, ChatMessage } from '../chat';

import type { IToolExecution, IToolStatus } from '../../hooks/useChatWebSocket';

/**
 * Admin chat pane for interacting with Claude AI about financial data.
 * Connects via WebSocket and displays real-time tool execution status.
 */
const AdminChatPane = () => {
  const { session, user } = useAuthState();
  const messagesEndRef = React.useRef<HTMLDivElement>(null);

  const { messages, toolStatus, toolExecutions, isProcessing, connectionStatus, error, sendMessage, clearMessages } =
    useChatWebSocket(session?.id ?? null);

  // Auto-scroll to bottom when new messages arrive (only on message changes, not status updates)
  React.useEffect(() => {
    // Use setTimeout to avoid stealing focus during scroll
    const timeout = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 0);
    return () => clearTimeout(timeout);
  }, [messages.length]);

  // Check admin access
  if (!user?.isAdmin) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-error-primary text-center">
          <div className="text-lg font-semibold">Admin access required</div>
          <div className="text-secondary text-sm">You must be an admin to access this feature.</div>
        </div>
      </div>
    );
  }

  const isConnecting = connectionStatus === 'connecting';
  const isConnected = connectionStatus === 'connected';
  const hasError = connectionStatus === 'error' || !!error;

  return (
    <div className="flex h-full w-full flex-col">
      {/* Header */}
      <div className="border-secondary flex items-center justify-between border-b px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="text-primary text-xl font-semibold">Financial Assistant</div>
          <ConnectionIndicator status={connectionStatus} />
        </div>
        {messages.length > 0 && (
          <button onClick={clearMessages} className="text-tertiary hover:text-secondary text-sm transition-colors">
            Clear chat
          </button>
        )}
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto flex max-w-3xl flex-col gap-4 p-6">
          {/* Connecting state */}
          {isConnecting && messages.length === 0 && (
            <div className="bg-secondary text-secondary rounded-lg p-4 text-sm">Connecting...</div>
          )}

          {/* Error message */}
          {hasError && !isConnecting && (
            <div className="bg-error-primary text-error-primary rounded-lg p-4 text-sm">
              {error ?? 'Connection error. Attempting to reconnect...'}
            </div>
          )}

          {/* Empty state */}
          {messages.length === 0 && !toolStatus && !hasError && !isConnecting && <EmptyState />}

          {/* Messages with tool executions shown ABOVE assistant messages */}
          {messages.map((message) => (
            <React.Fragment key={message.id}>
              {/* Show tool executions above assistant message */}
              {message.role === 'assistant' && message.toolExecutions && message.toolExecutions.length > 0 && (
                <ToolExecutionHistory toolExecutions={message.toolExecutions} />
              )}
              <ChatMessage message={message} />
            </React.Fragment>
          ))}

          {/* Current tool execution status - show during processing (including thinking phase) */}
          {isProcessing && <ToolExecutionPanel toolStatus={toolStatus} toolExecutions={toolExecutions} />}

          {/* Scroll anchor */}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input area */}
      <div className="border-secondary border-t p-4">
        <div className="mx-auto max-w-3xl">
          <ChatInput
            onSend={sendMessage}
            isDisabled={!isConnected || isConnecting || isProcessing}
            placeholder={
              isConnecting ? 'Connecting...' : isConnected ? 'Ask about your finances...' : 'Reconnecting...'
            }
          />
        </div>
      </div>
    </div>
  );
};

/**
 * Connection status indicator dot
 */
interface IConnectionIndicatorProps {
  status: 'connecting' | 'connected' | 'disconnected' | 'error';
}

const ConnectionIndicator = ({ status }: IConnectionIndicatorProps) => {
  const getStatusInfo = () => {
    switch (status) {
      case 'connected':
        return { color: 'bg-success-solid', label: 'Connected' };
      case 'connecting':
        return { color: 'bg-utility-warning-500 animate-pulse', label: 'Connecting' };
      case 'disconnected':
        return { color: 'bg-tertiary', label: 'Disconnected' };
      case 'error':
        return { color: 'bg-error-solid', label: 'Error' };
      default:
        return { color: 'bg-tertiary', label: 'Unknown' };
    }
  };

  const { color, label } = getStatusInfo();

  return (
    <div className="flex items-center gap-1.5">
      <div className={cx('size-2 rounded-full', color)} />
      <span className="text-tertiary text-xs">{label}</span>
    </div>
  );
};

/**
 * Empty state shown when no messages yet
 */
const EmptyState = () => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <div className="bg-brand-secondary mb-4 flex size-16 items-center justify-center rounded-full">
      <ChatIcon className="text-brand-solid size-8" />
    </div>
    <div className="text-primary mb-2 text-lg font-semibold">Financial Assistant</div>
    <div className="text-secondary mb-6 max-w-sm text-sm">
      Ask questions about your accounts, transactions, spending patterns, and recurring bills.
    </div>
    <div className="text-tertiary space-y-2 text-sm">
      <div>Try asking:</div>
      <ul className="text-secondary space-y-1">
        <li>"What is my current balance on my Chase credit card?"</li>
        <li>"How much did I spend last week?"</li>
        <li>"What are my biggest spending categories this month?"</li>
        <li>"What subscriptions do I have?"</li>
      </ul>
    </div>
  </div>
);

/**
 * Simple chat icon
 */
const ChatIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

/**
 * Tool execution panel showing current status and list of tools called (for in-progress requests)
 */
interface IToolExecutionPanelProps {
  toolStatus: IToolStatus | null;
  toolExecutions: IToolExecution[];
}

const ToolExecutionPanel = ({ toolStatus, toolExecutions }: IToolExecutionPanelProps) => {
  const isThinking = toolStatus?.status === 'thinking';

  return (
    <div className="bg-secondary border-primary rounded-lg border p-4">
      {/* Current status */}
      <div className="mb-3 flex items-center gap-2">
        <div
          className={cx(
            'size-2 rounded-full',
            isThinking ? 'bg-brand-solid animate-pulse' : 'bg-utility-warning-500 animate-pulse'
          )}
        />
        <span className="text-secondary text-sm font-medium">{isThinking ? 'Thinking...' : 'Executing tool...'}</span>
      </div>

      {/* Tool execution list */}
      {toolExecutions.length > 0 && (
        <div className="border-secondary space-y-3 border-t pt-3">
          <div className="text-tertiary text-xs font-medium uppercase tracking-wide">Tools being used</div>
          <div className="space-y-2">
            {toolExecutions.map((tool) => (
              <ToolExecutionItem key={tool.id} tool={tool} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Tool execution history shown after an assistant message (collapsed by default)
 */
interface IToolExecutionHistoryProps {
  toolExecutions: IToolExecution[];
}

const ToolExecutionHistory = ({ toolExecutions }: IToolExecutionHistoryProps) => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  return (
    <div className="bg-tertiary border-secondary ml-0 rounded-lg border">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="text-tertiary hover:text-secondary flex w-full items-center justify-between px-3 py-2 text-xs transition-colors"
      >
        <span className="font-medium">
          {toolExecutions.length} tool{toolExecutions.length > 1 ? 's' : ''} used
        </span>
        <ChevronIcon className={cx('size-4 transition-transform', isExpanded && 'rotate-180')} />
      </button>

      {isExpanded && (
        <div className="border-secondary space-y-2 border-t px-3 py-2">
          {toolExecutions.map((tool) => (
            <ToolExecutionItem key={tool.id} tool={tool} showDetails />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Individual tool execution item with expandable details
 */
interface IToolExecutionItemProps {
  tool: IToolExecution;
  showDetails?: boolean;
}

const ToolExecutionItem = ({ tool, showDetails = false }: IToolExecutionItemProps) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const hasInput = Object.keys(tool.toolInput).length > 0;

  return (
    <div className="space-y-1">
      <div className="flex items-start gap-2">
        <div
          className={cx(
            'mt-1 size-1.5 shrink-0 rounded-full',
            tool.status === 'running'
              ? 'bg-utility-warning-500 animate-pulse'
              : tool.status === 'error'
                ? 'bg-error-solid'
                : 'bg-success-solid'
          )}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-secondary font-mono text-xs font-medium">{tool.toolName}</span>
            {tool.status === 'complete' && <CheckIcon className="text-success-solid size-3 shrink-0" />}
            {tool.status === 'error' && <span className="text-error-primary text-xs">Error</span>}
            {hasInput && showDetails && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-tertiary hover:text-secondary text-xs underline"
              >
                {isExpanded ? 'hide' : 'details'}
              </button>
            )}
          </div>
          {tool.resultSummary && <div className="text-tertiary mt-0.5 text-xs">{tool.resultSummary}</div>}
        </div>
      </div>

      {isExpanded && hasInput && (
        <div className="bg-primary border-secondary ml-3.5 mt-1 rounded border p-2">
          <div className="text-tertiary mb-1 text-xs font-medium">Arguments:</div>
          <pre className="text-secondary overflow-x-auto text-xs">{JSON.stringify(tool.toolInput, null, 2)}</pre>
        </div>
      )}
    </div>
  );
};

/**
 * Chevron icon for expand/collapse
 */
const ChevronIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

/**
 * Simple check icon
 */
const CheckIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export default AdminChatPane;
